export type BulkUploadStep = 'upload' | 'review' | 'executing' | 'results';

export type ExecutionState =
    | 'not_started'
    | 'in_progress'
    | 'partial_users'
    | 'partial_invites'
    | 'completed'
    | 'failed_structure';

export type ExecuteResultLike = {
    status: 'success' | 'partial' | 'failed';
    failedPhase: 'structure' | 'users' | 'invites' | null;
    executionState: ExecutionState;
};

export type PreviewExecutionResourceLike<TExecuteResult extends ExecuteResultLike = ExecuteResultLike> = {
    executionState?: ExecutionState | null;
    executionResult?: TExecuteResult | null;
};

export type ExecuteResponseLike<TExecuteResult extends ExecuteResultLike = ExecuteResultLike> = {
    result?: { result?: TExecuteResult | null } | TExecuteResult | null;
};

type RestoredBulkUploadState<TExecuteResult extends ExecuteResultLike> = {
    step: Extract<BulkUploadStep, 'review' | 'executing' | 'results'>;
    executeResult: TExecuteResult | null;
};

const RESULT_STATES = new Set<ExecutionState>([
    'completed',
    'failed_structure',
    'partial_users',
    'partial_invites',
]);

export const normalizeExecuteResponseResult = <TExecuteResult extends ExecuteResultLike>(
    payload?: ExecuteResponseLike<TExecuteResult> | null,
): TExecuteResult | null => {
    const raw = payload?.result;

    if (!raw) {
        return null;
    }

    if ('result' in raw && raw.result && typeof raw.result === 'object' && 'status' in raw.result) {
        return raw.result;
    }

    if ('status' in raw) {
        return raw as TExecuteResult;
    }

    return null;
};

export const getRestoredBulkUploadState = <TExecuteResult extends ExecuteResultLike>(
    preview: PreviewExecutionResourceLike<TExecuteResult>,
): RestoredBulkUploadState<TExecuteResult> => {
    const executionState = preview.executionState || preview.executionResult?.executionState || 'not_started';

    if (executionState === 'in_progress') {
        return {
            step: 'executing',
            executeResult: null,
        };
    }

    if (RESULT_STATES.has(executionState) && preview.executionResult) {
        return {
            step: 'results',
            executeResult: preview.executionResult,
        };
    }

    return {
        step: 'review',
        executeResult: null,
    };
};

export const isBlockedExecuteResult = <TExecuteResult extends ExecuteResultLike>(
    result?: TExecuteResult | null,
) => Boolean(result && result.status === 'failed' && result.failedPhase === null);
